import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatePicker, message, Spin, Switch, Tabs } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ComingSoon } from '../../../shared/base-components/ComingSoon/ComingSoon';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import PartnerRoster from '../../TieringView/components/PartnerRoster/PartnerRoster';
import {
  partnershipSettingsService,
  PartnershipSettingData,
} from './PartnershipSettingsService';
import styles from './PartnershipSettings.module.css';

const DEFAULT_SETTINGS: PartnershipSettingData = {
  id: '',
  partnershipId: '',
  taxYear: '',
  fixedAssetModuleEnabled: true,
  calculationProcessingMode: 'CalcEngine',
  guaranteedPaymentsEnabled: false,
  taxYearEnd: null,
};

// ── Settings Tab Content ────────────────────────────────────────────────────────

const SettingsTab: React.FC<{
  settings: PartnershipSettingData;
  onAutoSave: (patch: Partial<PartnershipSettingData>) => Promise<void>;
}> = ({ settings, onAutoSave }) => {
  const [taxYearEndDate, setTaxYearEndDate] = useState<Dayjs | null>(
    settings.taxYearEnd ? dayjs(settings.taxYearEnd) : null,
  );
  const [savingDate, setSavingDate] = useState(false);

  const handleToggle = async (field: keyof PartnershipSettingData, value: boolean) => {
    await onAutoSave({ [field]: value });
  };

  const handleModeChange = async (mode: 'CalcEngine' | 'External') => {
    await onAutoSave({ calculationProcessingMode: mode });
  };

  const handleSaveDate = async () => {
    setSavingDate(true);
    try {
      await onAutoSave({ taxYearEnd: taxYearEndDate ? taxYearEndDate.toISOString() : null });
      message.success('Tax year end saved');
    } finally {
      setSavingDate(false);
    }
  };

  return (
    <div className={styles.settingsContent}>
      <div className={styles.sectionLabel}>Module &amp; Processing</div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Fixed Asset Module</div>
          <div className={styles.settingDesc}>
            Enable or disable the fixed asset module for this partnership. Disable for investment-only funds.
          </div>
        </div>
        <div className={styles.settingControl}>
          <Switch
            checked={settings.fixedAssetModuleEnabled}
            onChange={v => handleToggle('fixedAssetModuleEnabled', v)}
          />
        </div>
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Calculation Processing Mode</div>
          <div className={styles.settingDesc}>
            How calculations are processed. External partnerships are excluded from the engine but remain in tiering structure.
          </div>
        </div>
        <div className={styles.settingControl}>
          <div className={styles.modeToggle}>
            <button
              className={settings.calculationProcessingMode === 'CalcEngine' ? styles.modeBtnActive : styles.modeBtn}
              onClick={() => handleModeChange('CalcEngine')}
            >
              CalcEngine
            </button>
            <button
              className={settings.calculationProcessingMode === 'External' ? styles.modeBtnActive : styles.modeBtn}
              onClick={() => handleModeChange('External')}
            >
              External
            </button>
          </div>
        </div>
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Guaranteed Payments</div>
          <div className={styles.settingDesc}>
            Flag whether any partners receive guaranteed payments.
          </div>
        </div>
        <div className={styles.settingControl}>
          <Switch
            checked={settings.guaranteedPaymentsEnabled}
            onChange={v => handleToggle('guaranteedPaymentsEnabled', v)}
          />
        </div>
      </div>

      <div className={styles.sectionLabel}>Tax &amp; Accounting</div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <div className={styles.settingTitle}>Tax Year End</div>
          <div className={styles.settingDesc}>Partnership tax year-end date.</div>
        </div>
        <div className={styles.settingControl}>
          <div className={styles.dateRow}>
            <DatePicker
              value={taxYearEndDate}
              onChange={setTaxYearEndDate}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              size="small"
            />
            <button
              className={styles.btnPrimary}
              onClick={handleSaveDate}
              disabled={savingDate}
            >
              {savingDate ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── PartnershipSettings ─────────────────────────────────────────────────────────

export const PartnershipSettings: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [settings, setSettings]   = useState<PartnershipSettingData | null>(null);
  const [loading, setLoading]     = useState(false);
  const [savedVisible, setSavedVisible] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSettings = useCallback(async (partnershipId: string, year: string) => {
    setLoading(true);
    try {
      const data = await partnershipSettingsService.getSettings(partnershipId, year);
      setSettings(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === undefined) {
        setSettings({ ...DEFAULT_SETTINGS, partnershipId, taxYear: year });
      } else {
        message.error('Failed to load partnership settings');
        setSettings(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership) {
      fetchSettings(selectedPartnership.id, taxYear);
    } else {
      setSettings(null);
    }
  }, [selectedPartnership, taxYear, fetchSettings]);

  const showSaved = () => {
    setSavedVisible(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedVisible(false), 2500);
  };

  const handleAutoSave = useCallback(
    async (patch: Partial<PartnershipSettingData>) => {
      if (!selectedPartnership || !settings) return;
      const updated = { ...settings, ...patch };
      setSettings(updated);
      try {
        await partnershipSettingsService.updateSettings(
          selectedPartnership.id,
          taxYear,
          patch,
        );
        showSaved();
      } catch {
        message.error('Failed to save settings');
        setSettings(settings);
      }
    },
    [selectedPartnership, taxYear, settings],
  );

  const tabItems = [
    {
      key: 'settings',
      label: 'Settings',
      children: settings
        ? <SettingsTab settings={settings} onAutoSave={handleAutoSave} />
        : null,
    },
    {
      key: 'partners',
      label: 'Partners',
      children: selectedPartnership
        ? <PartnerRoster partnershipId={Number(selectedPartnership.id)} />
        : null,
    },
    {
      key: 'k1-input',
      label: 'External K-1 Input',
      children: <ComingSoon title="External K-1 Input" />,
    },
    {
      key: 'salt',
      label: 'State & Local Tax',
      children: <ComingSoon title="State & Local Tax Settings" />,
    },
  ];

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Settings</h1>
          {selectedPartnership && (
            <p className={styles.pageSub}>
              {selectedPartnership.name} · Configure before running any calculations.
            </p>
          )}
        </div>
      </div>

      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>⚙️</div>
          <div className={styles.emptyText}>Select a client and partnership to view settings</div>
        </div>
      ) : loading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : (
        <div className={styles.tabsWrap}>
          <Tabs items={tabItems} />
        </div>
      )}

      {savedVisible && (
        <div className={styles.savedBar}>
          <div className={styles.savedDot} />
          Settings saved automatically
        </div>
      )}
    </div>
  );
};
